import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useUser } from '../../context/UserContext';
import { Mail, Lock, LogIn, ArrowLeft, User, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';

const UserLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, register, googleLogin, isAuthenticated } = useUser();

    const [mode, setMode] = useState('login'); // 'login' or 'register'
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        mobile: ''
    });
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);

    // Get redirect URL from query params or default to home
    const redirectTo = new URLSearchParams(location.search).get('redirect') || '/';

    // Redirect if already logged in
    React.useEffect(() => {
        if (isAuthenticated) {
            navigate(redirectTo, { replace: true });
        }
    }, [isAuthenticated, navigate, redirectTo]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (mode === 'login') {
            if (!formData.email || !formData.password) {
                toast.error('Please enter email and password');
                return;
            }
        } else {
            if (!formData.name || !formData.email || !formData.password) {
                toast.error('Please fill in all required fields');
                return;
            }
            if (formData.password.length < 6) {
                toast.error('Password must be at least 6 characters');
                return;
            }
        }

        setLoading(true);

        try {
            if (mode === 'login') {
                const userData = await login(formData.email, formData.password);
                toast.success(`Welcome back, ${userData.name}!`);
            } else {
                const userData = await register(formData.name, formData.email, formData.password, formData.mobile);
                toast.success(`Welcome, ${userData.name}! Account created successfully.`);
            }
            navigate(redirectTo, { replace: true });
        } catch (error) {
            console.error('Auth error:', error);
            toast.error(error.response?.data?.error || `${mode === 'login' ? 'Login' : 'Registration'} failed`);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        setGoogleLoading(true);
        try {
            const userData = await googleLogin(credentialResponse.credential);
            toast.success(`Welcome, ${userData.name}!`);
            navigate(redirectTo, { replace: true });
        } catch (error) {
            console.error('Google login error:', error);
            toast.error(error.response?.data?.error || 'Google login failed');
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleGoogleError = () => {
        toast.error('Google Sign-In failed. Please try again.');
    };

    return (
        <PublicLayout>
            <SEO
                title="Login / Sign Up"
                description="Sign in or create an account to book retiring rooms at railway stations across India. Access advance booking and manage your reservations."
                canonical="/user/login"
                keywords="retiring room login, sign up retiring room, railway station room booking login"
                noIndex
            />
            <div className="min-h-[70vh] bg-gradient-to-b from-midnight-50 to-white py-12">
                <div className="max-w-md mx-auto px-4">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-railway-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <User className="w-8 h-8 text-railway-600" />
                        </div>
                        <h1 className="text-2xl font-display font-bold text-midnight-900">
                            {mode === 'login' ? 'Login to Book' : 'Create Account'}
                        </h1>
                        <p className="text-midnight-600 mt-2">
                            {mode === 'login'
                                ? 'Sign in to continue with your booking'
                                : 'Register to make advance bookings'
                            }
                        </p>
                    </div>

                    {/* Form Card */}
                    <div className="card">
                        <div className="p-6">
                            {/* Mode Toggle */}
                            <div className="flex gap-2 bg-midnight-50 rounded-xl p-1.5 mb-6">
                                <button
                                    type="button"
                                    onClick={() => setMode('login')}
                                    className={`
                    flex-1 py-2.5 px-4 rounded-lg font-medium transition-all
                    ${mode === 'login'
                                            ? 'bg-white text-midnight-900 shadow-sm'
                                            : 'text-midnight-600 hover:text-midnight-900'
                                        }
                  `}
                                >
                                    Login
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMode('register')}
                                    className={`
                    flex-1 py-2.5 px-4 rounded-lg font-medium transition-all
                    ${mode === 'register'
                                            ? 'bg-white text-midnight-900 shadow-sm'
                                            : 'text-midnight-600 hover:text-midnight-900'
                                        }
                  `}
                                >
                                    Register
                                </button>
                            </div>

                            {/* Google Sign-In */}
                            <div className="mb-6">
                                <div className="flex justify-center">
                                    {googleLoading ? (
                                        <div className="flex items-center gap-2 py-3">
                                            <LoadingSpinner size="sm" />
                                            <span className="text-midnight-600">Signing in with Google...</span>
                                        </div>
                                    ) : (
                                        <GoogleLogin
                                            onSuccess={handleGoogleSuccess}
                                            onError={handleGoogleError}
                                            useOneTap
                                            theme="outline"
                                            size="large"
                                            width="100%"
                                            text="continue_with"
                                            shape="rectangular"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="relative mb-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-midnight-200"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-4 bg-white text-midnight-500">or continue with email</span>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Name (Register only) */}
                                {mode === 'register' && (
                                    <div>
                                        <label className="block text-sm font-medium text-midnight-700 mb-2">
                                            Full Name *
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Your full name"
                                                className="input-field pl-12"
                                                required
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-midnight-700 mb-2">
                                        Email Address *
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="your@email.com"
                                            className="input-field pl-12"
                                            autoComplete="email"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div>
                                    <label className="block text-sm font-medium text-midnight-700 mb-2">
                                        Password *
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="••••••••"
                                            className="input-field pl-12"
                                            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                                            required
                                        />
                                    </div>
                                    {mode === 'register' && (
                                        <p className="text-xs text-midnight-500 mt-1">
                                            Minimum 6 characters
                                        </p>
                                    )}
                                </div>

                                {/* Mobile (Register only) */}
                                {mode === 'register' && (
                                    <div>
                                        <label className="block text-sm font-medium text-midnight-700 mb-2">
                                            Mobile Number (Optional)
                                        </label>
                                        <div className="relative">
                                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
                                            <input
                                                type="tel"
                                                name="mobile"
                                                value={formData.mobile}
                                                onChange={handleChange}
                                                placeholder="Your mobile number"
                                                className="input-field pl-12"
                                                maxLength={10}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Submit */}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full flex items-center justify-center gap-2 mt-6"
                                >
                                    {loading ? (
                                        <>
                                            <LoadingSpinner size="sm" />
                                            <span>{mode === 'login' ? 'Signing in...' : 'Creating account...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <LogIn className="w-5 h-5" />
                                            <span>{mode === 'login' ? 'Sign In' : 'Create Account'}</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Back Link */}
                    <div className="text-center mt-6">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-midnight-600 hover:text-railway-600 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
};

export default UserLogin;
